import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Users, Shield, MoreVertical, AlertCircle, CheckCircle } from 'lucide-react';
import * as sdk from 'matrix-js-sdk';

const ClaraChatRoom = ({ room, onClose, matrixClient }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [matrixRoom, setMatrixRoom] = useState(null);
  const messagesEndRef = useRef(null);

  // Initialize Matrix client and room
  useEffect(() => {
    const initializeMatrix = async () => {
      try {
        if (!matrixClient) {
          // Create Matrix client if not provided
          const client = sdk.createClient({
            baseUrl: "https://clara360.de",
            accessToken: localStorage.getItem('clara_matrix_token'),
            userId: localStorage.getItem('clara_matrix_user_id'),
          });
          
          await client.startClient();
          setIsConnected(true);
          
          // Get or create room
          const rooms = client.getRooms();
          let targetRoom = rooms.find(r => r.name === room.name);
          
          if (!targetRoom) {
            // Create room if it doesn't exist
            const roomOptions = {
              name: room.name,
              topic: `Clara360 Chat: ${room.name}`,
              preset: 'private_chat',
              initial_state: [{
                type: 'm.room.encryption',
                content: {
                  algorithm: 'm.megolm.v1.aes-sha2'
                }
              }]
            };
            
            const { room_id } = await client.createRoom(roomOptions);
            targetRoom = client.getRoom(room_id);
          }
          
          setMatrixRoom(targetRoom);
          loadMessages(targetRoom);
          
        } else {
          setIsConnected(true);
          setMatrixRoom(matrixClient.getRoom(room.id));
          loadMessages(matrixClient.getRoom(room.id));
        }
      } catch (err) {
        console.error('Matrix initialization error:', err);
        setError('Verbindung zu Matrix Server fehlgeschlagen');
        // Fall back to demo messages
        loadDemoMessages();
      }
    };

    initializeMatrix();
  }, [room, matrixClient]);

  const loadMessages = (matrixRoom) => {
    if (!matrixRoom) {
      loadDemoMessages();
      return;
    }

    try {
      const timeline = matrixRoom.getLiveTimeline();
      const events = timeline.getEvents();
      
      const messageEvents = events
        .filter(event => event.getType() === 'm.room.message')
        .map(event => ({
          id: event.getId(),
          sender: event.getSender(),
          content: event.getContent().body,
          timestamp: new Date(event.getTs()),
          encrypted: event.isEncrypted(),
          event: event
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      setMessages(messageEvents);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading messages:', err);
      loadDemoMessages();
    }
  };

  const loadDemoMessages = () => {
    const demoMessages = [
      {
        id: 1,
        sender: 'admin@clara360.de',
        content: `Willkommen im ${room.name}! 🎉`,
        timestamp: new Date(Date.now() - 3600000),
        encrypted: true
      },
      {
        id: 2,
        sender: 'hausmeister@clara360.de',
        content: room.lastMessage || 'Hallo zusammen! Die Matrix-Integration wird gerade eingerichtet.',
        timestamp: new Date(Date.now() - 1800000),
        encrypted: true
      },
      {
        id: 3,
        sender: 'system',
        content: 'Demo-Modus: Nachrichten werden noch nicht persistent gespeichert.',
        timestamp: new Date(Date.now() - 900000),
        encrypted: false
      }
    ];
    
    setMessages(demoMessages);
    setIsLoading(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      if (matrixRoom && isConnected) {
        // Send via Matrix
        await matrixRoom.sendTextMessage(newMessage);
        setNewMessage('');
      } else {
        // Demo mode - add message locally
        const message = {
          id: Date.now(),
          sender: 'Sie',
          content: newMessage,
          timestamp: new Date(),
          encrypted: true
        };

        setMessages(prev => [...prev, message]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Nachricht konnte nicht gesendet werden');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getSenderDisplayName = (sender) => {
    if (sender === 'Sie') return sender;
    if (sender.includes('@')) {
      return sender.split('@')[0];
    }
    return sender;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{room.name}</h2>
              <div className="flex items-center gap-2 text-sm text-purple-100">
                <Users className="w-4 h-4" />
                <span>{room.members} Mitglieder</span>
                {room.encrypted && (
                  <>
                    <Shield className="w-4 h-4" />
                    <span>Verschlüsselt</span>
                  </>
                )}
                <div className="flex items-center gap-1">
                  {isConnected ? (
                    <>
                      <CheckCircle className="w-3 h-3 text-green-400" />
                      <span className="text-xs">Matrix Verbunden</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3 h-3 text-yellow-400" />
                      <span className="text-xs">Demo Modus</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
              <MoreVertical className="w-5 h-5" />
            </button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className="flex flex-col">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {getSenderDisplayName(message.sender).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm dark:text-white">
                          {getSenderDisplayName(message.sender)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTime(message.timestamp)}
                        </span>
                        {message.encrypted && (
                          <Shield className="w-3 h-3 text-green-500" />
                        )}
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 max-w-md">
                        <p className="text-sm dark:text-white">{message.content}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <div className="p-4 border-t dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nachricht eingeben..."
                className="w-full p-3 pr-12 border dark:border-gray-600 rounded-lg resize-none dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="1"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <Shield className="w-3 h-3 text-green-500" />
            <span>Ende-zu-Ende verschlüsselt • Matrix Protocol</span>
            {!isConnected && (
              <span className="text-yellow-600">• Demo Modus aktiv</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaraChatRoom;

